// evertel
import { injectable, inject, decorate } from '@evertel/di'
import { APIDataMedia, SchemaProps } from '@evertel/types'
import { Api } from '@evertel/api'
import { MediaUploadService, NormalizedMedia, UploadableMedia } from '@evertel/media'

interface MediaBundle {
    uploaded: NormalizedMedia[] | number[]      // Already uploaded media (with full metadata)
    uploadQueue: UploadableMedia[]   // Files queued for upload
    deleteQueue: number[]            // IDs of media to delete
}

// The input can be the raw ID array, the rich bundle, or nothing
type MediaFieldValue = number[] | MediaBundle | null | undefined

class MediaUtils {

    public errors = []

    constructor(
        private api: Api,
        private mediaUploadService: MediaUploadService
    ) {
    }

    clearErrors = () => {
        this.errors = []
    }

    /**
     * Processes all media fields in a document's data based on its schema.
     * Handles uploads, deletions, and flattens rich media bundles to ID arrays.
     * Returns a new object — does not mutate the original data.
     */
    processDocumentMedia = async (
        platform: 'web' | 'mobile' = 'mobile',
        data: Record<string, any>,
        documentId: number,
        schema: SchemaProps['layouts']['full']
    ): Promise<Record<string, any>> => {

        this.errors = []

        return this.processMediaInSchema(schema, data, documentId)
    }

    /**
     * Recursively walks the schema and data together, processing media fields.
     * Returns a new object with media fields flattened to ID arrays.
     */
    private processMediaInSchema = async (
        schema: SchemaProps['layouts']['full'],
        data: Record<string, any>,
        documentId: number
    ): Promise<Record<string, any>> => {

        if (!schema || !data) {
            return data ?? {}
        }

        // Shallow copy — we'll replace values as needed
        const result = { ...data }

        for (const field of schema) {
            // Structural containers — recurse into items at same data level
            if (field.type === 'row' || field.type === 'container') {
                if (field.items) {
                    const processed = await this.processMediaInSchema(field.items, result, documentId)
                    // copy all processed values into result
                    Object.assign(result, processed)
                }
            }
            // Sub-form/list — recurse for each item in the data array
            else if (field.type === 'list' && field.id) {
                const items = data[field.id]
                if (Array.isArray(items)) {
                    const schemaItems = field.items || (field.item ? [field.item] : [])
                    const processedItems = []
                    for (const item of items) {
                        const processed = await this.processMediaInSchema(schemaItems, item, documentId)
                        processedItems.push(processed)
                    }
                    result[field.id] = processedItems
                }
            }
            // Media field — process uploads/deletes and flatten to IDs
            else if (field.type === 'media' && field.id) {
                const value = data[field.id]
                if (value !== undefined) {
                    result[field.id] = await this.processMediaValue(value, documentId)
                }
            }
        }

        return result
    }

    /**
     * Processes a single media field value:
     * - If already an array of IDs, returns as-is
     * - If a rich MediaBundle, handles deletions, uploads, and returns flattened IDs
     */
    private processMediaValue = async (value: MediaFieldValue, documentId: number): Promise<number[] | null> => {

        // Already processed — just an array of IDs
        if (Array.isArray(value)) {
            return value
        }

        // No media or not a bundle
        if (!value || typeof value.uploaded === 'undefined') {
            return []
        }

        // Delete queued media
        await Promise.all(value.deleteQueue.map(async (mediaId: number) => {
            try {
                await this.api.Routes.Document.delMediaById(documentId, mediaId)
            } catch (e) {
                console.error(e)
            }
        }))

        // Upload queued media
        let added: number[] = []
        for (const media of value.uploadQueue) {
            this.mediaUploadService.addUploadableMedia(documentId, 'document', media)
        }

        const uploadableMedia = this.mediaUploadService.getMediaUploadsForModel(documentId, 'document')

        await this.mediaUploadService.startMediaUploads(uploadableMedia, async (mediaUploads, completedUpload, error) => {
            if (error) {
                this.errors.push(error)
            }
            added = mediaUploads
                .filter((m) => m.state === 'complete')
                .map(m => m.remoteMedia?.id)
        }).catch(() => {
            // swallow the thrown error, we already captured it in callback
        })

        // Convert uploaded array to array of IDs (handles both full objects and raw IDs)
        const uploaded = value.uploaded?.map((u: APIDataMedia | number) =>
            typeof u === 'object' ? u.id : u
        ) || []

        return [...uploaded, ...added]
    }
}

decorate(injectable(), MediaUtils)
decorate(inject(Api), MediaUtils, 0)
decorate(inject(MediaUploadService), MediaUtils, 1)

export { MediaUtils }
